import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, BadgeCheck, UserCircle } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { DetailField } from '@/components/common/DetailSection'
import { FormActions, FormField, FormSection } from '@/components/common/FormSection'
import { BRAZILIAN_STATES } from '@/components/users/UserFormFields'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Skeleton } from '@/components/ui/skeleton'
import { isLawyerLike, roleLabel } from '@/lib/roles'

export function ProfilePage() {
  const { user, updateProfile } = useAuth()
  const navigate = useNavigate()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!user) return

    const formData = new FormData(event.currentTarget)

    const updateData = {
      firstName: formData.get('firstName') as string,
      lastName: formData.get('lastName') as string,
      email: formData.get('email') as string,
      oabNumber: formData.get('oabNumber') as string || undefined,
      oabState: formData.get('oabState') as string || undefined
    }

    try {
      setSaving(true)
      setError(null)
      setSuccess(null)
      const result = await updateProfile(updateData)

      if (result.success) {
        setSuccess('Perfil atualizado com sucesso!')
        setTimeout(() => setSuccess(null), 5000)
      } else {
        setError(result.message || 'Erro ao atualizar perfil')
      }
    } catch (err) {
      setError('Erro ao atualizar perfil')
      console.error('Error updating profile:', err)
    } finally {
      setSaving(false)
    }
  }

  if (!user) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <span className="sr-only">Carregando perfil...</span>
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-64" />
      </div>
    )
  }

  const isLawyer = isLawyerLike(user.role)

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-col gap-2">
        <Button variant="ghost" size="sm" className="-ml-2 self-start" onClick={() => navigate(-1)}>
          <ArrowLeft className="size-4" /> Voltar
        </Button>
        <div className="flex items-center gap-2">
          <UserCircle className="size-6 shrink-0 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">Meu Perfil</h1>
        </div>
        <p className="text-sm text-muted-foreground">Gerencie suas informações pessoais</p>
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {success && (
        <p role="status" className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-primary">
          {success}
        </p>
      )}

      <form onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
        <FormSection title="Informações Pessoais" icon={UserCircle}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Nome *" htmlFor="firstName">
              <Input type="text" id="firstName" name="firstName" required defaultValue={user.firstName} />
            </FormField>
            <FormField label="Sobrenome *" htmlFor="lastName">
              <Input type="text" id="lastName" name="lastName" required defaultValue={user.lastName} />
            </FormField>
          </div>
          <FormField label="E-mail *" htmlFor="email">
            <Input type="email" id="email" name="email" required defaultValue={user.email} />
          </FormField>
          <dl className="border-t border-border pt-4">
            <DetailField label="Função">{roleLabel(user.role)}</DetailField>
          </dl>
        </FormSection>

        {isLawyer && (
          <FormSection
            title="Dados da OAB"
            icon={BadgeCheck}
            description="Estes dados são necessários para permitir a importação de publicações do DJEN"
            className="sm:grid-cols-2"
          >
            <FormField label="Número da OAB" htmlFor="oabNumber">
              <Input
                type="text"
                id="oabNumber"
                name="oabNumber"
                defaultValue={user.oabNumber || ''}
                placeholder="Ex: 123456"
                maxLength={20}
              />
            </FormField>
            <FormField label="Estado da OAB" htmlFor="oabState">
              <NativeSelect id="oabState" name="oabState" defaultValue={user.oabState || ''}>
                <option value="">Selecione um estado</option>
                {BRAZILIAN_STATES.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label} ({state.value})
                  </option>
                ))}
              </NativeSelect>
            </FormField>
          </FormSection>
        )}

        <FormActions>
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancelar
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </FormActions>
      </form>
    </div>
  )
}
